import {
  ChangeEvent,
  forwardRef,
  HTMLAttributes,
  HTMLProps,
  MouseEventHandler,
  Ref,
  useState,
} from 'react';
import { createComponent, createHook } from 'reakit-system';
import { Attach as AttachIcon } from '@mos-cat/ds-icons';
import { SystemStyleObject } from '@styled-system/css';
import { pxToRem } from '@mos-cat/ds-theme';
import { Box } from '../../Box/Box';
import { kind, Kind } from '../../Button/variants';
import { StylesOptions, useStyles } from '../../hooks/useStyles';
import { focus } from '../../utils/focusable';

type InputFileProps = {
  formats: string[];
  formErrorId?: string;
  inputProps: HTMLProps<HTMLInputElement>;
  onClick: MouseEventHandler<HTMLInputElement>;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

export const useInputFileLabel = createHook<
  { kind: Kind } & StylesOptions,
  HTMLAttributes<HTMLLabelElement>
>({
  name: 'InputFileLabel',
  keys: ['kind'],
  compose: [useStyles],
  useOptions(options) {
    return {
      ...options,
      styles: {
        ...options.styles,
        display: 'inline-flex',
        alignItems: 'center',
        flexGrow: 0,
        justifyContent: 'center',
        pt: 'btn48PaddingTop',
        px: 'btn48PaddingX',
        pb: 'btn48PaddingBottom',
        fontSize: 'btn48FontSize',
        fontWeight: 'btn48FontWeight',
        lineHeight: 'btn48LineHeight',
        minHeight: pxToRem(48),
        borderRadius: 'button',
        border: 1,
        maxWidth: 1,
        width: [1, 'fileUploadBtnWidth'],

        '& svg': {
          width: 'btn48IconSize',
          height: 'btn48IconSize',
        },
        ...(kind(options) as SystemStyleObject),
      },
    };
  },
});

const StyledLabel = createComponent({
  as: 'label',
  useHook: useInputFileLabel,
  memo: true,
});

export const InputFile = forwardRef(
  (
    { formats, formErrorId, inputProps, onClick, onChange }: InputFileProps,
    ref: Ref<HTMLInputElement>
  ) => {
    const [labelFocused, setLabelFocused] = useState<boolean>(false);
    const [inputKey, setInputKey] = useState<number>(0);
    const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      setInputKey(inputKey + 1);
      onChange(event);
    };
    const handleInputFocus = () => {
      setLabelFocused(true);
    };
    const handleInputBlur = () => {
      setLabelFocused(false);
    };

    return (
      <Box>
        <StyledLabel
          kind="secondary"
          className={labelFocused ? 'focused' : undefined}
          styles={labelFocused ? focus : undefined}
        >
          <input
            key={inputKey}
            ref={ref}
            {...inputProps}
            aria-describedby={formErrorId}
            type="file"
            accept={formats.map((format) => `.${format}`).join(',')}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onClick={onClick}
            onChange={onInputChange}
            {...useStyles({ styles: { opacity: 0, width: 0, height: 0 } })}
          />
          <Box display="inline-block" as={AttachIcon} />
          <Box display="inline-block" ml="sp-8" aria-hidden="true">
            Добавить файл
          </Box>
        </StyledLabel>
      </Box>
    );
  }
);
